import {Component, inject, Input, OnDestroy} from '@angular/core';
import {DatePipe} from "@angular/common";
import {Subject, takeUntil} from "rxjs";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from "@angular/material/card";
import {MatError, MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatSelectModule} from "@angular/material/select";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {ICharityPayee} from "../../../interfaces/payee/ICharityPayee";
import {ICharityOwedForPayee} from "../../../interfaces/payee/ICharityOwedForPayee";
import {IPayeePayoutAllocation} from "../../../interfaces/payee/IRecordPayeePayoutResult";
import {ICharityPayeeAssignment} from "../../../interfaces/payee/ICharityPayeeAssignment";
import {IPayeePaymentSummary} from "../../../interfaces/payee/IPayeePaymentSummary";
import {CharityPayeeService} from "../../../services/charity-payee.service";
import {TableExportButtonsComponent} from "../../../components/table-export-buttons/table-export-buttons.component";
import {ITableExportRequest, TableExportService} from "../../../services/table-export.service";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {SnackbarService} from "../../../services/snackbar.service";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {PipesModule} from "../../../pipes/pipes.module";
import {LoadingCardContentComponent} from "../../../shared/loading-card-content/loading-card-content.component";
import {HasPermissionDirective} from "../../../directives/permissions/has-permission";
import {PermissionTypes} from "../../../enum/PermissionTypes";

/**
 * Central payees for one game (#1411).
 *
 * Charley: "some charity's get paid to one central bank account, e.g. all the holland college teams get one
 * payout to holland college (who is not a unique charity in the game) ... need a way to inform that during
 * charity setup, and then ability to change it later if needed."
 *
 * Three jobs on one screen, because they are one workflow: name the account, say which charities are remitted
 * to it, and record the cheque when it goes out. The screen shows the split before it is committed — a payment
 * to a payee becomes one payout row per member charity, and an admin should see those parts rather than trust
 * that the total landed somewhere sensible.
 */
@Component({
  selector: 'app-charity-payees',
  standalone: true,
  imports: [
    TableExportButtonsComponent,
    ReactiveFormsModule,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatButton,
    MatIconButton,
    MatIcon,
    MatSelectModule,
    PipesModule,
    LoadingCardContentComponent,
    HasPermissionDirective,
    DatePipe
  ],
  templateUrl: './charity-payees.component.html',
  styleUrls: ['./charity-payees.component.scss']
})
export class CharityPayeesComponent implements OnDestroy {

  private tableExportService: TableExportService = inject(TableExportService);
  private charityPayeeService: CharityPayeeService = inject(CharityPayeeService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private destroy$ = new Subject<void>();

  @Input() set gameData(gameP: IGameQueryResult) {
    this.activeGame = gameP;
    this.payees = [];
    this.assignments = [];
    this.selectedPayee = null;
    this.members = [];
    this.payments = [];
    this.lastRecordedAllocations = [];
    this.refresh();
  }

  public activeGame: IGameQueryResult | null = null;
  public payees: ICharityPayee[] = [];
  public assignments: ICharityPayeeAssignment[] = [];
  public selectedPayee: ICharityPayee | null = null;
  public members: ICharityOwedForPayee[] = [];
  /** Payments already made to the selected payee, so a mis-keyed cheque can be voided and re-recorded. */
  public payments: IPayeePaymentSummary[] = [];
  /** The split of the payment just recorded, shown so the cheque's parts are visible after the fact too. */
  public lastRecordedAllocations: IPayeePayoutAllocation[] = [];
  public isSubmitting = false;
  public uiState = UIStateEnum.ShowData;
  protected readonly UIStateEnum = UIStateEnum;
  protected readonly permissionTypes = PermissionTypes;

  public newPayeeForm = new FormGroup({
    name: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(200)])
  });

  public paymentForm = new FormGroup({
    amount: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    paymentDate: new FormControl<string | null>(CharityPayeesComponent.todayIso()),
    referenceNumber: new FormControl<string | null>(null, [Validators.maxLength(100)]),
    note: new FormControl<string | null>(null)
  });

  private static todayIso(): string {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');

    return `${now.getFullYear()}-${month}-${day}`;
  }

  get today(): string {
    return CharityPayeesComponent.todayIso();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get activePayees(): ICharityPayee[] {
    return this.payees.filter(payeeP => payeeP.Active);
  }

  /** What the payee's charities are owed in total — what a cheque covering them all would come to. */
  get totalOwedToMembers(): number {
    return this.members.reduce((totalP, memberP) => totalP + Math.max(memberP.AmountOwed, 0), 0);
  }

  /** The amount typed, in cents, so it can be compared with the balances the screen is showing. */
  get paymentAmountCents(): number {
    const amount = this.paymentForm.controls.amount.value;

    return amount ? Math.round(amount * 100) : 0;
  }

  /**
   * Whether this payment is more than the payee's charities are owed between them.
   *
   * It is allowed — the cheque left the bank and the ledger has to match it — but what is owed is settled first
   * and the excess is spread evenly across every member, so an overpayment leaves each of them holding a small
   * credit. An unexplained credit balance is what makes the next period's figures untrustworthy, which is why the
   * note below is required rather than suggested. The server refuses it without one too (599).
   */
  get isOverpayment(): boolean {
    return this.paymentAmountCents > this.totalOwedToMembers;
  }

  /** The amount by which this payment exceeds the total owed, in cents, for the warning. */
  get overpaymentCents(): number {
    return Math.max(this.paymentAmountCents - this.totalOwedToMembers, 0);
  }

  get canRecordPayment(): boolean {
    if (this.isOverpayment && !this.paymentForm.controls.note.value?.trim()) {
      return false;
    }

    return !!this.selectedPayee && this.members.length > 0 && this.paymentForm.valid && !this.isSubmitting;
  }

  refresh(): void {
    if (!this.activeGame) return;

    this.uiState = UIStateEnum.ShowLoading;

    this.charityPayeeService.fetchByGameId(this.activeGame.Id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (payeesP) => {
        this.payees = payeesP ?? [];
        this.uiState = UIStateEnum.ShowData;

        // Keep the selection across a refresh, so recording a payment does not bounce the admin back to the
        // top of the screen with the cheque they just entered no longer on show.
        if (this.selectedPayee) {
          const still_there = this.payees.find(payeeP => payeeP.Id === this.selectedPayee!.Id);

          this.selectedPayee = still_there ?? null;

          if (this.selectedPayee) {
            this.loadMembers(this.selectedPayee);
          }
        }
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });

    this.charityPayeeService.fetchAssignments(this.activeGame.Id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (assignmentsP) => this.assignments = assignmentsP ?? [],
        error: (err) => this.errorHandlingService.displayPageLevelErrorMessage(err)
      });
  }

  onCreatePayee(): void {
    if (!this.activeGame || this.newPayeeForm.invalid || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    this.charityPayeeService.createPayee(this.activeGame.Id, this.newPayeeForm.controls.name.value!.trim())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.snackbarService.openSuccessfulSnackBar('Payee added successfully!');
          this.newPayeeForm.reset();
          this.isSubmitting = false;
          this.refresh();
        },
        error: (err) => {
          this.errorHandlingService.displayPageLevelErrorMessage(err);
          this.isSubmitting = false;
        }
      });
  }

  onToggleActive(payeeP: ICharityPayee): void {
    if (!this.activeGame) return;

    this.charityPayeeService.updatePayee(this.activeGame.Id, payeeP.Id, payeeP.Name, !payeeP.Active)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.snackbarService.openSuccessfulSnackBar(payeeP.Active ? 'Payee deactivated.' : 'Payee reactivated.');
          this.refresh();
        },
        error: (err) => this.errorHandlingService.displayPageLevelErrorMessage(err)
      });
  }

  onAssignCharity(assignmentP: ICharityPayeeAssignment, payeeIdP: string | null): void {
    if (!this.activeGame) return;

    this.charityPayeeService.assignCharity(this.activeGame.Id, assignmentP.CharityId, payeeIdP || null)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.snackbarService.openSuccessfulSnackBar('Charity payee updated.');
          this.refresh();
        },
        error: (err) => this.errorHandlingService.displayPageLevelErrorMessage(err)
      });
  }

  onSelectPayee(payeeP: ICharityPayee): void {
    this.selectedPayee = payeeP;
    this.lastRecordedAllocations = [];
    this.loadMembers(payeeP);
  }

  private loadMembers(payeeP: ICharityPayee): void {
    if (!this.activeGame) return;

    this.charityPayeeService.fetchMembers(payeeP.Id, this.activeGame.Id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (membersP) => this.members = membersP ?? [],
        error: (err) => this.errorHandlingService.displayPageLevelErrorMessage(err)
      });

    this.charityPayeeService.fetchPayments(payeeP.Id, this.activeGame.Id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (paymentsP) => this.payments = paymentsP ?? [],
        error: (err) => this.errorHandlingService.displayPageLevelErrorMessage(err)
      });
  }

  /**
   * Voids a whole payment. One charity's share cannot be voided or edited on its own — the rows are one cheque,
   * and changing one would leave the rest claiming a payment that no longer adds up.
   */
  onVoidPayment(paymentP: IPayeePaymentSummary): void {
    if (!this.activeGame) return;

    this.charityPayeeService.voidPayment(this.activeGame.Id, paymentP.PayoutBatchId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.snackbarService.openSuccessfulSnackBar('Payment voided successfully!');
          this.lastRecordedAllocations = [];
          this.refresh();
        },
        error: (err) => this.errorHandlingService.displayPageLevelErrorMessage(err)
      });
  }

  /** Fills the amount with everything the payee's charities are owed — the common case for a period payment. */
  onFillTotalOwed(): void {
    this.paymentForm.controls.amount.setValue(this.totalOwedToMembers / 100);
  }

  onRecordPayment(): void {
    if (!this.canRecordPayment || !this.activeGame || !this.selectedPayee) {
      return;
    }

    this.isSubmitting = true;
    const form_value = this.paymentForm.getRawValue();

    this.charityPayeeService.recordPayeePayout({
      GameId: this.activeGame.Id,
      CharityPayeeId: this.selectedPayee.Id,
      // The form collects dollars; the ledger is in cents.
      PayoutAmount: Math.round((form_value.amount ?? 0) * 100),
      PaymentDate: form_value.paymentDate || null,
      ReferenceNumber: form_value.referenceNumber?.trim() || null,
      Note: form_value.note?.trim() || null
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (resultP) => {
        this.snackbarService.openSuccessfulSnackBar('Payment recorded successfully!');
        this.lastRecordedAllocations = resultP?.Allocations ?? [];
        this.isSubmitting = false;
        this.paymentForm.reset({paymentDate: CharityPayeesComponent.todayIso()});
        this.refresh();
      },
      error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
        this.isSubmitting = false;
      }
    });
  }

  /**
   * The payee list as shown, deactivated ones included: a payout already made names the payee it was paid to,
   * so a list that quietly drops them cannot be reconciled against the bank.
   */
  private buildPayeesExportRequest(): ITableExportRequest<ICharityPayee> {
    return {
      title: 'Central payees',
      subtitle: this.activeGame?.Name ?? '',
      summary: [
        {label: 'Payees', value: `${this.payees.length}`},
        {label: 'Active', value: `${this.activePayees.length}`}
      ],
      rows: this.payees,
      columns: [
        {header: 'Payee', value: payeeP => payeeP.Name},
        {header: 'Status', value: payeeP => payeeP.Active ? 'Active' : 'Inactive'}
      ],
      fileName: 'central-payees'
    };
  }

  /**
   * Who each charity is paid through — the answer to "why did Holland College get one cheque for six teams",
   * which is the question this screen exists for and the one an auditor asks a year later.
   */
  private buildAssignmentsExportRequest(): ITableExportRequest<ICharityPayeeAssignment> {
    return {
      title: 'Who each charity is paid through',
      subtitle: this.activeGame?.Name ?? '',
      summary: [
        {label: 'Charities', value: `${this.assignments.length}`},
        {
          label: 'Paid through a payee',
          value: `${this.assignments.filter(assignmentP => !!assignmentP.PayeeId).length}`
        }
      ],
      rows: this.assignments,
      columns: [
        {
          header: 'Charity',
          value: assignmentP => assignmentP.CharityActive
            ? assignmentP.CharityName
            : `${assignmentP.CharityName} (inactive)`
        },
        {
          header: 'Paid through',
          // Spelled out rather than left blank: an empty cell in a spreadsheet reads as missing data, and
          // "paid directly" is a decision somebody made.
          value: assignmentP => this.payees.find(payeeP => payeeP.Id === assignmentP.PayeeId)?.Name ?? 'Paid directly'
        }
      ],
      fileName: 'charity-payee-assignments'
    };
  }

  onExportPayeesCsv(): void {
    this.tableExportService.exportCsv(this.buildPayeesExportRequest());
  }

  onExportPayeesPdf(): void {
    this.tableExportService.exportPdf(this.buildPayeesExportRequest());
  }

  onExportAssignmentsCsv(): void {
    this.tableExportService.exportCsv(this.buildAssignmentsExportRequest());
  }

  onExportAssignmentsPdf(): void {
    this.tableExportService.exportPdf(this.buildAssignmentsExportRequest());
  }
}
